import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import { useGetClientCarsQuery } from 'services/api/clients.api';
import type { ClientCar } from 'services/types/clientCar';
import { NAVY, RADIUS_CARD, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import InfoItem from './InfoItem';

interface ClientCarsSectionProps {
  clientId: number;
}

const createInfoItem = (label: string, value: React.ReactNode | null | undefined, fullWidth = false) => {
  const displayValue = value === null || value === undefined || value === '' ? '-' : value;
  return <InfoItem key={label} label={label} value={displayValue} fullWidth={fullWidth} />;
};

const carTitle = (car: ClientCar): string => {
  if (car.licensePlate) return car.licensePlate;
  const brandModel = [car.brand, car.model].filter(Boolean).join(' ');
  return brandModel || `Vehículo #${car.id}`;
};

const renderCarDetails = (car: ClientCar) => (
  <Grid container spacing={1}>
    {createInfoItem('Marca', car.brand)}
    {createInfoItem('Modelo', car.model)}
    {createInfoItem('Año', car.year)}
    {createInfoItem('Patente', car.licensePlate)}
    {createInfoItem('Versión', car.version)}
    {createInfoItem('Kilometraje', car.km != null ? car.km.toLocaleString('es-CL') : null)}
    {createInfoItem('VIN', car.vin)}
    {createInfoItem(
      'Última mantención',
      car.lastMaintenanceDate ? new Date(car.lastMaintenanceDate).toLocaleDateString('es-CL') : null
    )}
    {createInfoItem(
      'Fecha de compra',
      car.purchaseDate ? new Date(car.purchaseDate).toLocaleDateString('es-CL') : null
    )}
    {car.warranty && (
      <>
        {createInfoItem('Garantía (plan)', car.warranty.plan)}
        {createInfoItem(
          'Garantía (vence)',
          car.warranty.expirationDate ? new Date(car.warranty.expirationDate).toLocaleDateString('es-CL') : null
        )}
        {createInfoItem('Garantía (km límite)', car.warranty.kmLimit)}
        {createInfoItem('Garantía usada', car.warranty.used ? 'Sí' : 'No')}
      </>
    )}
  </Grid>
);

const ClientCarsSection: React.FC<ClientCarsSectionProps> = ({ clientId }) => {
  const { data, isLoading } = useGetClientCarsQuery(String(clientId));
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const cars = data?.cars ?? [];

  return (
    <Box>
      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: RADIUS_CARD }} />
          ))}
        </Box>
      )}

      {!isLoading && cars.length === 0 && (
        <Typography sx={{ color: alpha(NAVY, 0.5), fontStyle: 'italic' }}>
          Este cliente no tiene vehículos registrados.
        </Typography>
      )}

      {!isLoading && cars.length > 0 && (
        <Box>
          {cars.map((car) => (
            <Accordion
              key={car.id}
              expanded={expanded[car.id] || false}
              onChange={(_, isExpanded) => setExpanded((prev) => ({ ...prev, [car.id]: isExpanded }))}
              sx={{
                mb: 0.5,
                boxShadow: '0px 1px 4px rgba(21, 24, 58, 0.08)',
                borderRadius: `${RADIUS_CARD} !important`,
                backgroundColor: SURFACE_CONTAINER_LOWEST,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: '0 0 4px 0' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: alpha(NAVY, 0.55) }} />}
                sx={{
                  backgroundColor: SURFACE_CONTAINER_LOWEST,
                  borderRadius: `${RADIUS_CARD} ${RADIUS_CARD} 0 0`,
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: NAVY }}>
                  {carTitle(car)}
                </Typography>
                {car.licensePlate && (car.brand || car.model) && (
                  <Typography variant="caption" sx={{ color: alpha(NAVY, 0.55), ml: 'auto' }}>
                    {[car.brand, car.model].filter(Boolean).join(' ')}
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ py: 1 }}>{renderCarDetails(car)}</AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ClientCarsSection;
